import Link from "next/link";
import base from "lib/base";
import styles from "./NewsHero.module.css";

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}

function truncateWords(text, maxWords) {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(" ") + "...";
}

function getExcerpt(html, maxWords = 30) {
  const text = stripHtml(html);
  return truncateWords(text, maxWords);
}

function resolveCdnImage(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = String(path).replace(/^\/+/, "");
  const cdnRoot = String(base.cdnUrl || "").replace(/\/+$/, "");
  return `${cdnRoot}/450/${normalized}`;
}

function resolveCdnUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = String(path).replace(/^\/+/, "");
  const cdnRoot = String(base.cdnUrl || "").replace(/\/+$/, "");
  return `${cdnRoot}/${normalized}`;
}

export default function NewsHero({ featured = [] }) {
  if (featured.length === 0) return null;

  const [main, ...secondary] = featured;

  return (
    <div className={styles.heroContainer}>
      {/* Main Featured Article - 9 columns */}
      {main && (
        <Link
          href={`/news/${main.slug || main._id}`}
          className={styles.mainFeatured}
        >
          <div className={styles.mainImage}>
            {main.pictures && main.pictures[0] ? (
              <img
                src={resolveCdnImage(main.pictures[0])}
                alt={main.name || "Featured article"}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                loading="eager"
                decoding="async"
              />
            ) : (
              <div className={styles.placeholder} />
            )}
          </div>
          <div className={styles.mainContent}>
            <h2 className={styles.mainTitle}>{main.name}</h2>
            {main.createdAt && (
              <p className={styles.publishDate}>{formatDate(main.createdAt)}</p>
            )}
            <p className={styles.mainExcerpt}>
              {getExcerpt(main.details || main.intro || main.description, 40)}
            </p>
            {main.authors && (
              <div className={styles.authorInfo}>
                {main.authors.avatar && (
                  <img
                    src={resolveCdnUrl(main.authors.avatar)}
                    alt={main.authors.name || "Author"}
                    width={32}
                    height={32}
                    className={styles.authorAvatar}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <span className={styles.authorName}>{main.authors.name}</span>
              </div>
            )}
          </div>
        </Link>
      )}

      {/* Secondary Featured Article - 3 columns */}
      {secondary.length > 0 && (
        <div className={styles.secondaryFeatured}>
          {secondary.map((article, index) => (
            <Link
              key={article._id || index}
              href={`/news/${article.slug || article._id}`}
              className={styles.secondaryCard}
            >
              <div className={styles.secondaryImage}>
                {article.pictures && article.pictures[0] ? (
                  <img
                    src={resolveCdnImage(article.pictures[0])}
                    alt={article.name || "Article"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className={styles.placeholder} />
                )}
              </div>
              <div className={styles.secondaryContent}>
                <h3 className={styles.secondaryTitle}>{article.name}</h3>
                {article.createdAt && (
                  <p className={styles.publishDate}>
                    {formatDate(article.createdAt)}
                  </p>
                )}
                <p className={styles.secondaryExcerpt}>
                  {getExcerpt(
                    article.details || article.intro || article.description,
                    25,
                  )}
                </p>
                {article.authors && (
                  <div className={styles.authorInfo}>
                    {article.authors.avatar && (
                      <img
                        src={resolveCdnUrl(article.authors.avatar)}
                        alt={article.authors.name || "Author"}
                        width={24}
                        height={24}
                        className={styles.authorAvatar}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <span className={styles.authorName}>
                      {article.authors.name}
                    </span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
